import React from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { BrandIdentity } from '@/core/api/entities/brandIdentity'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import Icon from '@/shared/components/atoms/Icon'
import { Label } from '@/shared/components/atoms/Label/Label'

interface RegisterStep7Props {
	form: UseFormReturn<RegisterRequestDTO>
	brandIdentities: BrandIdentity[]
	isLoading: boolean
	onFormSubmit: () => void
	onPrevStep: () => void
}

export const RegisterStep7: React.FC<RegisterStep7Props> = ({
	form,
	brandIdentities,
	isLoading,
	onFormSubmit,
}) => {
	const { t } = useTranslation('auth')

	const selectedIdentities = form.watch('brand_identities') || []

	const isFormValid =
		selectedIdentities.length > 0 &&
		Object.keys(form.formState.errors).length === 0

	const handleIdentityToggle = (identityId: number) => {
		const currentIdentities = form.getValues('brand_identities') || []
		const isSelected = currentIdentities.includes(identityId)

		if (isSelected) {
			const newIdentities = currentIdentities.filter((id) => id !== identityId)
			form.setValue('brand_identities', newIdentities)
		} else {
			if (currentIdentities.length < 3) {
				form.setValue('brand_identities', [...currentIdentities, identityId])
			}
		}
	}

	const handleFormSubmit = () => {
		onFormSubmit()
	}

	return (
		<div className="space-y-8 flex flex-col justify-between h-full">
			<div className="space-y-4">
				<div className="space-y-4">
					<h1 className="register-title text-xl font-bold ">
						{t('register.brandProfileTitle')}
					</h1>
					<p className="max-w-2xl mx-auto">
						{t('register.brandProfileInstructions')}
					</p>
				</div>
				<div className="space-y-6">
					<Label className="mb-2 block">
						{t('register.brandIdentitiesLabel')}
					</Label>
					<Controller
						control={form.control}
						name="brand_identities"
						render={({ fieldState: { error } }) => (
							<div>
								<div className="flex flex-wrap justify-left gap-1.5 max-w-7xl mx-auto">
									{brandIdentities.map((identity) => (
										<button
											key={identity.id}
											type="button"
											onClick={() => handleIdentityToggle(identity.id)}
											className={`flex flex-row justify-center gap-1 items-center px-2.5 cursor-pointer py-1 rounded-full text-sm font-medium transition-all ${
												selectedIdentities.includes(identity.id)
													? 'dark:bg-white dark:text-black bg-black text-white'
													: 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-[#292929] dark:text-white dark:hover:bg-[#292929]/80'
											}`}
										>
											{selectedIdentities.includes(identity.id) && (
												<Icon
													name="checkmark-black"
													width="16px"
													height="16px"
												/>
											)}
											{identity.name}
										</button>
									))}
								</div>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
					<p className="text-muted-foreground mt-2">
						{' '}
						{selectedIdentities.length}/3 {t('registerStep6.selected')}
					</p>
				</div>
			</div>

			<div className="mt-8 flex gap-4">
				<Button
					onClick={handleFormSubmit}
					className="px-8 py-3 cursor-pointer flex-1 h-10 text-sm font-medium rounded-full"
					disabled={isLoading || !isFormValid}
				>
					{isLoading ? 'Loading...' : t('registerStep6.finish')}
				</Button>
			</div>
		</div>
	)
}
